import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { type ChatSessionUpdate, chatCreateSession, chatGetSessionQueryKey, chatUpdateSession } from "@/api";
import { type ChatHost, chatHost } from "./chatHost";
import { chatSessionRequest } from "./useChatSession";
import { useOpenCreatedChat } from "./useChatSwitchKey";

interface Draft {
  agentUuid: string | null;
  modelId: string | null;
  reasoningEffort: string | null;
  spaceUuid?: string | null;
  brandUuid?: string | null;
}

/** The picks as they are now, if they changed since the chat was asked for. */
export function picksSince(sent: Draft, now: Draft): ChatSessionUpdate | null {
  const same =
    sent.agentUuid === now.agentUuid &&
    sent.modelId === now.modelId &&
    sent.reasoningEffort === now.reasoningEffort &&
    (sent.brandUuid ?? null) === (now.brandUuid ?? null);
  if (same) return null;
  return {
    agentUuid: now.agentUuid,
    reasoningEffort: now.reasoningEffort,
    brandUuid: now.brandUuid ?? null,
    ...(now.modelId ? { modelId: now.modelId } : {}),
  };
}

/**
 * The chat to open once it's created. Picks made while it was being created are saved to it first (MHS-1038), or
 * they'd be lost when it opens; if that fails, it opens with what it was created with, which its menus then show.
 */
async function withPicksMadeMeanwhile<T>(uuid: string, created: T, sent: Draft, now: Draft) {
  const picks = picksSince(sent, now);
  if (!picks) return created;
  try {
    return (await chatUpdateSession({ path: { session_uuid: uuid }, body: picks, throwOnError: true })).data;
  } catch {
    return created;
  }
}

/**
 * Returns the open chat's uuid, creating the chat first for a new one (used
 * when something needs a session before the first message, e.g. uploads).
 * Concurrent callers share one create.
 */
export function useEnsureSession(
  sessionUuid: string | undefined,
  draft: Draft,
  host: ChatHost = chatHost,
  /** `useChatSwitchKey`: another new chat creates a chat of its own. */
  chatKey = 0,
) {
  const openCreated = useOpenCreatedChat();
  const queryClient = useQueryClient();
  const creating = useRef<Promise<string> | null>(null);
  // The agent, model and brand can still change while the chat is being created
  const latest = useRef({ draft, chatKey });
  latest.current = { draft, chatKey };
  // Uploads queued before the page re-rendered with the created chat still call the function they were given,
  // so it keeps answering with that chat until the page opens another new chat (MHS-917: two quick pastes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: chatKey is a trigger (new chat to new chat, MHS-1039)
  useEffect(() => {
    if (!sessionUuid) creating.current = null;
  }, [sessionUuid, chatKey]);
  return (): Promise<string> => {
    if (sessionUuid) return Promise.resolve(sessionUuid);
    if (creating.current) return creating.current;
    const attempt = (async () => {
      const sent = draft;
      const modelId = sent.modelId;
      if (!modelId) throw new Error("No model available");
      const { data } = await chatCreateSession({
        body: {
          modelId,
          agentUuid: sent.agentUuid,
          reasoningEffort: sent.reasoningEffort,
          ...(sent.spaceUuid ? { spaceUuid: sent.spaceUuid } : {}),
          ...(sent.brandUuid ? { brandUuid: sent.brandUuid } : {}),
          ...(host.source ? { source: host.source } : {}),
        },
        throwOnError: true,
      });
      const uuid = data.data.uuid;
      // Only picks made in this chat: once the person has moved to another one, those are that chat's
      const now = latest.current;
      const opened = now.chatKey === chatKey ? await withPicksMadeMeanwhile(uuid, data, sent, now.draft) : data;
      // It opens known to have no message yet, so its agent and model can still be picked (MHS-1038)
      queryClient.setQueryData(chatGetSessionQueryKey(chatSessionRequest(uuid)), opened);
      openCreated(host.pathFor(uuid));
      return uuid;
    })();
    creating.current = attempt;
    attempt.catch(() => {
      if (creating.current === attempt) creating.current = null;
    });
    return attempt;
  };
}
